import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Gear } from '../shared/gear.model';
import { LonglineGear } from '../shared/longline-gear.model';
import { PelagicGear } from '../shared/pelagic-gear.model';
import { PotGear } from '../shared/pot-gear.model';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupItem } from '../shared/lookup-item.model';
import { Subscription } from 'rxjs';
import { LookupDataService } from '../shared/lookup-data.service';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { nanoid } from 'nanoid';


enum GroundfishGearType { Pot = "Pot", Longline = "Longline", Pelagic = "Pelagic" };
enum DSBGSubType { Standard = "Standard", Linked = "Linked", StandardNight = "Standard-Night", LinkedNight = "Linked-Night" };
const DSBGSubTypeId = [261, 262, 265, 266];
enum Fishery { Groundfish = "Groundfish", HMS = "HMS"};

/**
 * We display the user gear(s) in this dialog and handle editing or adding of gears.
 * Workflow is: Select/Add gear -> If adding, ask for type -> Display details in form, empty if adding new, prepopulated if editing
 */
@Component({
    selector: 'app-gear-select-dialog',
    templateUrl: './gear-select-dialog.component.html',
    styleUrls: ['./gear-select-dialog.component.css'],
    standalone: false
})
export class GearSelectDialogComponent implements OnInit, OnDestroy {

  /**
   * The screen we came from determines which screens on this dialog are shown.
   */
  fromScreen: string = "";
  /**
   * What view is this dialog currently displaying?
   */
  currentScreen: string = "";

  /**
   * When adding a new gear, this stores the selected type
   */
  selectedType: GroundfishGearType = GroundfishGearType.Pot;
  selectedFishery: Fishery;
  isNew: boolean = false;

  /**
   * Index into the user gear array
   */
  selectedGear: number = -1;

  // current gear name
  currGearName: string = "";

  /**
   * This is emitted by the detail edit screen on completion.
   */
  selectedPot: PotGear = new PotGear();
  /**
   * This is emitted by the detail edit screen on completion.
   */
  selectedLine: LonglineGear = new LonglineGear();
  /**
   * This is emitted by the detail edit screen on completion.
   */
  selectedPelagic: PelagicGear = new PelagicGear();

  /**
   * Subscription to get gear lookup data from lookup data
   */
  gearTypeSub!: Subscription;
  /**
   * Pot types from lookup data
   */
  potTypes: LookupItem[] = [];
  /**
   * Longline types from lookup data
   */
  longlineTypes: LookupItem[] = [];
  /**
   * Pelagic types from lookup data
   */
  pelagicTypes: LookupItem[] = [];

  constructor(
    public dataSync: LocalStorageService,
    public dialogRef: MatDialogRef<GearSelectDialogComponent>,
    private dialog: MatDialog,
    private lookupService: LookupDataService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { screen: string, gearIndex: number}) {
      this.selectedFishery = dataSync.current_user().groundfishFishery ? Fishery.Groundfish : Fishery.HMS;

    if (data.screen == 'Settings') {  // Don't filter the gear list on settings page
      //this.userGear = LocalStorageService.user_gear;
      //LocalStorageService.user_gear$.subscribe(val => this.userGear = val);
    } else if (data.screen == 'List') {
      //this.userGear = LocalStorageService.user_gear.filter(g => g.fishery == 'Groundfish');
      //LocalStorageService.user_gear$.subscribe(val => val.filter(g => g.fishery == 'Groundfish'));
    }

   }

   /**
    * Get the lookup data and set the proper screen
    */
  ngOnInit(): void {
    this.gearTypeSub = this.lookupService.getGearTypeObservable().subscribe((value: LookupItem[]) => {
      this.potTypes = value.filter(gt => gt.lookupType == "POTTYPE");
      this.longlineTypes = value.filter(gt => gt.lookupType == "LONGLINETYPE");
      this.pelagicTypes = value.filter(gt => gt.lookupType == "PELAGICTYPE");
    });

    this.fromScreen = this.data.screen;
    this.selectedGear = this.data.gearIndex;

    // depending on what the previous screen was, go to that screen upon dialog cancellation
    if (this.fromScreen === "Settings") {
      if (this.selectedGear !== -1) {
        this.editCurrentGear();
      }
      else {
        this.addNewGear();
      }
    }
    else {
      this.changeScreen("List");
    }
  }

  /**
   * Go back to list or close dialog
   */
  goBack(): void {
    if (this.fromScreen === "Settings") {
      this.changeScreen("Close");
    }
    else {
      this.changeScreen("List");
    }
  }

  /**
   * Get values from type screen
  */  
  fromTypeScreen(typeDetails: any): void {
    this.currGearName = typeDetails.name;
    //If hms type, save the gear here..
    if (typeDetails.hmsType != '') this.newHMSGear(typeDetails);
    else this.changeScreen(typeDetails.screen);
    
  }

  /**
   * 
   * @param screen Go to this screen
   */
  changeScreen(screen: string): void {
    if (screen === "Close") {
      this.onNoClick();
    }
    else {
      this.currentScreen = screen;
    }
  }


  /**
   * 'Select' the gear, that is assign the gear to one of the 'selectedGear' data members
   * @param ind Index of selected gear
   */
  selectGear(ind: number) {
    this.selectedGear = ind;
    const gear = this.dataSync.user_gear()[ind];
    switch (gear.gearSubtype.name) {
      case GroundfishGearType.Pot:
        this.selectedPot = gear as PotGear;
        break;
      case GroundfishGearType.Longline:
        this.selectedLine = gear as LonglineGear;
        break;
      case GroundfishGearType.Pelagic:
        this.selectedPelagic = gear as PelagicGear;
        break;
    }
  }

  /**
   * Go back to gear type select screen
   * @param type Which selected type?
   * @returns 
   */
  cancelButtonClick(type: string): void {

    this.selectedGear = -1;

    // save gear type on type selection
    switch (type) {
      case "Pot":
        this.selectedType = GroundfishGearType.Pot;
        break;
      case "Longline":
        this.selectedType = GroundfishGearType.Longline;
        break;
      case "Pelagic":
        this.selectedType = GroundfishGearType.Pelagic;
        break;
      default:
        this.selectedType = GroundfishGearType.Pot;
        break;
    }

    if (this.fromScreen === "Settings") {
      this.changeScreen("Close");
      return;
    }
    this.changeScreen(this.isNew ? 'Type' : 'List');
  }


  /**
   * Set selected gear when adding a new gear; then go to Type screen
   */ 
  addNewGear() {
    this.isNew = true;
    this.selectedLine = new LonglineGear();
    this.selectedPelagic = new PelagicGear();
    this.selectedPot = new PotGear();
    this.changeScreen("Type");
  }


  /**
   * Edit currently selected gear, set the correct selected geat then go to the corresponding 'Detail' screen.
   */ 
  editCurrentGear() {
    if (this.selectedGear < 0) return;
    
    this.isNew = false;
    const gear = this.dataSync.user_gear()[this.selectedGear];
    switch (gear.type) {
      case GroundfishGearType.Pot:
        this.selectedPot = gear as PotGear;
        this.changeScreen("Detail-Pot");
        break;
      case GroundfishGearType.Longline:
        this.selectedLine = gear as LonglineGear;
        this.changeScreen("Detail-Longline");
        break;
      case GroundfishGearType.Pelagic:
        this.selectedPelagic = gear as PelagicGear;
        this.changeScreen("Detail-Pelagic");
        break;
    }
  }

  /**
   * Add selected gear to the set
   */ 
  confirmCurrentGear(): void {
    this.dialogRef.close(this.selectedGear);
  }

  /**
   * When last letter in base 26 is Z, update the more significant digits
   */ 
  updateSigFigs26(i: number, codeEnd: number, codeString: string): string {

    for (i; i > -1; i--) {

      let code: number = codeString.charCodeAt(i);

      if (code < codeEnd) {
        return codeString.substring(0, i) + String.fromCharCode(code + 1) + codeString.substring(i + 1);
      }
      else {
        codeString = codeString.substring(0, i) + 'A' + codeString.substring(i + 1);
      }

    }
    return codeString += 'A';
  }

  /**
   * Set the gear name
   */ 
  setGearName(): string {
    // current gear not custom named
    if (!this.currGearName) {

      // check if there's at least one gear in the gear list
      let length: number = this.dataSync.user_gear().length;

      if (length > 0) {
        // set bounds for base 26 counter
        let codeStart: number = 'A'.charCodeAt(0);
        let codeEnd: number = 'Z'.charCodeAt(0);
        let codeString: string = 'A';

        while (codeString.length < 4) {

          let i: number = codeString.length - 1;

          for (let code: number = codeStart; code <= codeEnd; code++) {

            // get code string to compare against gear identifiers
            codeString = codeString.substring(0, i) + String.fromCharCode(code);

            // check code string against all gear identifiers
            if (!this.dataSync.user_gear().some(g => g.identifier == codeString)) return codeString;
            
          }

          // last letter is Z
          codeString = this.updateSigFigs26(i - 1, codeEnd, codeString.substring(0, i) + 'A');
        }

      }

      // currGearName undefined, userGear is empty list
      return 'A';
    }

    // currGearname defined
    return this.currGearName;
  }

  /**
   * From the detail screen, receive the new gear created or the updated existing gear here.
   * We receive it as the base gear type and translate it to a gear subclass.
   * Assign an id and store it in the database
   */ 
  newGear(gear: Gear): void {
    //If this is from an update create a new gear that copies
    let newGear = gear.type === "Pot" ? gear as PotGear : gear.type === "Longline" ? gear as LonglineGear : gear as PelagicGear;
    newGear.fishery = 'Groundfish';
    if (!this.isNew) this.currGearName = gear.identifier;
    console.log("New Gear:");

    if (this.isNew || !this.dataSync.user_gear()[this.selectedGear].isEqual(newGear)) {
      newGear.gearID = 'gear-' + nanoid();
      newGear.identifier = newGear.identifier == '' ? this.setGearName() : newGear.identifier;
      this.dataSync.storeGear(gear);
    }

    // back to settings screen
    if (this.data.screen == 'Settings') {
      this.goBack();
      this.selectedGear = -1;
    }
    // set new gear as current set gear
    else {
      this.selectGear(this.dataSync.user_gear().length - 1);
      this.goBack();
      this.confirmCurrentGear();
    }
  }


  newHMSGear(typeDetails: any) {
    let hmsGear = new Gear();
    hmsGear.fishery = 'HMS';
    hmsGear.type = typeDetails.hmsType;
    hmsGear.gearSubtype = new LookupItem(typeDetails.dsbgSubType, DSBGSubTypeId[Object.keys(DSBGSubType).indexOf(typeDetails.dsbgSubType)]);

    if (this.isNew) {
      hmsGear.gearID = 'gear-' + nanoid();
      hmsGear.identifier =  this.setGearName();
      this.dataSync.storeGear(hmsGear);
    }

    // back to settings screen
    if (this.data.screen == 'Settings') {
      this.goBack();
      this.selectedGear = -1;
    }
    // set new gear as current set gear
    else {
      this.selectedGear = this.dataSync.user_gear().length - 1;
      this.goBack();
      this.confirmCurrentGear();
    }
  }


  /**
   * Delete current gear {@link GearSelectDialogComponent.selectedGear}
   */
  deleteGear(): void {
    if (this.selectedGear === -1) {
      this.goBack();
      return;
    }

    let id = this.dataSync.user_gear()[this.selectedGear].gearID;
    if (this.dataSync.user_trip().sets.some(s => s.gear.gearID == id)) {
      console.log('Cannot delete');
      this.toastr.error('Gear is currently attached to one or more set(s), cannot delete.', '', { timeOut: 5000, extendedTimeOut: 5000 });
      return;
    }


    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { screen: 'deletegear' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataSync.deleteGear(id).then(res => {
          if (res) {
            this.dataSync.user_gear.update(gearList => gearList.filter(g => g.gearID != id));
            this.selectedGear = -1;
            this.goBack();
          } else {
            alert('deletion failed');
          }
        })
      }     
    });
  }

  // close the gear dialog
  onNoClick(): void {
    this.dialogRef.close(-1);
  }


  ngOnDestroy() {
    this.gearTypeSub.unsubscribe();
  }
}
